/* eslint-disable complexity */
import { getCustomAccessTextFromAttributes } from '@neb/permissions';
import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';

import { getCognitoConfig } from '../neb-api-client/src/permissions-api-client';
import { AUTH_PAGE_TYPES } from '../neb-lit-components/src/components/security/neb-2fa-auth-page';
import {
  KAFKA_DOWN_ERROR_MESSAGE,
  setMfaInvalidCode,
  setMfaValidating,
  setMfaGoToLogin,
} from '../neb-login/neb-session-state';
import { store } from '../neb-redux/neb-redux-store';

import { formatToE164 } from './formatters';

const CODE_MISMATCH_ERROR_NAME = 'CodeMismatchException';

export const COGNITO_TYPES = {
  PATIENT: 'patient',
  PRACTICE: 'practice',
  SUPPORT: 'support',
};

export const MFA_TYPES = {
  PHONE: 'phone',
  TOTP: 'totp',
  DISABLED: 'disabled',
};

let userPoolConfig = {};
let userPool;
let initPromise;

const __init = async (type = COGNITO_TYPES.PATIENT) => {
  userPoolConfig = await getCognitoConfig(type);
  userPool = new AmazonCognitoIdentity.CognitoUserPool({
    UserPoolId: userPoolConfig.userPoolId,
    ClientId: userPoolConfig.bookingAppClientId,
    endpoint: userPoolConfig.endpoint || undefined,
  });
};

export const createCognitoUser = username => {
  const user = new AmazonCognitoIdentity.CognitoUser({
    Username: username.toLowerCase(),
    Pool: userPool,
  });

  if (userPoolConfig.endpoint) {
    user.setAuthenticationFlowType('USER_PASSWORD_AUTH');
  }

  return user;
};

export const init = (type = COGNITO_TYPES.PATIENT) => {
  if (!initPromise) {
    initPromise = __init(type);
  }

  return initPromise;
};

export const teardown = () => {
  initPromise = null;
  userPoolConfig = {};
  userPool = null;
};

const getSession = (idToken, refreshToken, currentTenantId) => {
  const customAccess = getCustomAccessTextFromAttributes(idToken.payload);
  const access = customAccess && JSON.parse(customAccess);

  let tenantId = currentTenantId;
  let tenantIds;

  if (access) {
    tenantIds = Object.keys(access);

    if (tenantIds && tenantIds.length === 1) {
      tenantId = tenantIds[0];
    } else tenantId = tenantIds.find(id => id === tenantId);
  }

  return {
    refreshToken,
    idToken,
    accessToken: idToken.jwtToken,
    tenantId,
    tenantIds,
    access,
    id: idToken.payload['cognito:username'],
  };
};

const getAuthDetails = (username, password) =>
  new AmazonCognitoIdentity.AuthenticationDetails({
    Username: username.toLowerCase(),
    Password: password,
  });

const doesPasswordExist = (password, email) => {
  if (!email) return false;
  const authenticationDetails = getAuthDetails(email, password);
  const cognitoUser = createCognitoUser(email);
  return new Promise(resolve => {
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: () => resolve(true),
      onFailure: () => resolve(false),
      newPasswordRequired: () => resolve(true),
    });
  });
};

const EMPTY_ACCESS = '{}';

export const authenticateUser = async ({
  username,
  password,
  type = COGNITO_TYPES.PATIENT,
  mfaHandler,
}) => {
  try {
    await init(type);
  } catch (err) {
    console.log(err);
    throw new Error(KAFKA_DOWN_ERROR_MESSAGE);
  }

  const authenticationDetails = getAuthDetails(username, password);
  const cognitoUser = createCognitoUser(username);
  return new Promise((resolve, reject) => {
    const authHandler = {
      onSuccess: result => {
        if (
          getCustomAccessTextFromAttributes(result.getIdToken().payload) ===
          EMPTY_ACCESS
        ) {
          reject(new Error('No Access'));
        } else {
          resolve({
            ...getSession(result.getIdToken(), result.getRefreshToken()),
            type,
          });
        }
      },
      onFailure: err => {
        if (err.code === 'PasswordResetRequiredException') {
          resolve({
            expiredPassword: true,
          });
        } else reject(err);
      },
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        if (
          getCustomAccessTextFromAttributes(userAttributes) === EMPTY_ACCESS
        ) {
          reject(new Error('No Access'));
        } else {
          delete userAttributes.email_verified;
          resolve({
            changePassword: async (password, email) => {
              const passwordExists = await doesPasswordExist(password, email);
              if (passwordExists) return false;
              return new Promise((resolve, reject) => {
                cognitoUser.completeNewPasswordChallenge(
                  password,
                  requiredAttributes,
                  {
                    onSuccess: resolve,
                    onFailure: reject,
                  },
                );
              });
            },
          });
        }
      },
    };

    const handleMfaChallenge = (
      authPageType,
      mfaMethod,
      errorMessage,
      challengeParameters,
    ) => {
      if (mfaHandler) {
        mfaHandler(
          userInputCode => {
            store.dispatch(setMfaValidating());
            cognitoUser.sendMFACode(
              userInputCode,
              {
                ...authHandler,
                onFailure: err => {
                  if (err.name === CODE_MISMATCH_ERROR_NAME) {
                    store.dispatch(setMfaInvalidCode());
                    return;
                  }
                  store.dispatch(setMfaGoToLogin());
                  authHandler.onFailure(err);
                },
              },
              mfaMethod,
            );
          },
          authPageType,
          challengeParameters,
        );
      } else {
        reject(new Error(errorMessage));
      }
    };

    authHandler.totpRequired = _challengeName => {
      handleMfaChallenge(
        AUTH_PAGE_TYPES.TOTP,
        'SOFTWARE_TOKEN_MFA',
        'TOTP authentication required but no handler provided',
      );
    };

    authHandler.mfaRequired = (_challengeName, challengeParameters) => {
      handleMfaChallenge(
        AUTH_PAGE_TYPES.SMS,
        'SMS_MFA',
        'SMS MFA authentication required but no handler provided',
        challengeParameters,
      );
    };

    cognitoUser.authenticateUser(authenticationDetails, authHandler);
  });
};

export const renew = async options => {
  await init(options.type);
  const cognitoUser = createCognitoUser(options.idToken.payload.email);
  return new Promise((resolve, reject) => {
    cognitoUser.refreshSession(
      {
        getToken: () => options.refreshToken.token,
      },
      (err, session) => {
        if (err) {
          reject(err);
        } else {
          resolve({
            ...getSession(
              session.getIdToken(),
              session.getRefreshToken(),
              options.tenantId,
            ),
            type: options.type,
          });
        }
      },
    );
  });
};

export const signOut = async options => {
  await init();
  const cognitoUser = createCognitoUser(options.idToken.payload.email);

  if (cognitoUser != null) {
    return new Promise((resolve, _reject) => {
      resolve(cognitoUser.signOut());
    });
  }

  return undefined;
};

export const forgotPassword = async email => {
  await init();
  const cognitoUser = createCognitoUser(email);
  return new Promise((resolve, reject) => {
    cognitoUser.forgotPassword({
      onSuccess: data => {
        resolve(data);
      },
      onFailure: err => {
        reject(err);
      },
    });
  });
};

export const confirmPassword = async (newPassword, verificationCode, email) => {
  await init();
  const cognitoUser = createCognitoUser(email);
  const passwordExists = await doesPasswordExist(newPassword, email);
  if (passwordExists) return false;
  return new Promise((resolve, reject) => {
    cognitoUser.confirmPassword(verificationCode, newPassword, {
      onSuccess: (data = {}) => {
        resolve(data);
      },
      onFailure: err => {
        reject(err);
      },
    });
  });
};

export const changePassword = async (oldPassword, newPassword, email) => {
  await init();
  const authenticationDetails = getAuthDetails(email, oldPassword);
  const cognitoUser = createCognitoUser(email);
  return new Promise((resolve, reject) => {
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: () => {
        cognitoUser.changePassword(
          oldPassword,
          newPassword,
          (error, success) => {
            if (error) {
              reject(error);
            } else {
              resolve(success);
            }
          },
        );
      },
      onFailure: err => {
        reject(err);
      },
    });
  });
};

/**
 * Sets MFA preferences for a user
 * @param {string} email - User's email
 * @param {Object} refreshToken - User's refresh token
 * @param {string} mfaType - The MFA type to enable: MFA_TYPES.PHONE, MFA_TYPES.TOTP, or MFA_TYPES.DISABLED
 * @returns {Promise<Object>} - Result of the operation
 */
const setUserMfaPreference = async (
  email,
  refreshToken,
  mfaType = MFA_TYPES.DISABLED,
) => {
  await init();
  const cognitoUser = createCognitoUser(email);

  let smsMfaSettings = null;
  let totpMfaSettings = null;

  if (mfaType === MFA_TYPES.PHONE) {
    smsMfaSettings = {
      Enabled: true,
      PreferredMfa: true,
    };
  } else if (mfaType === MFA_TYPES.TOTP) {
    totpMfaSettings = {
      Enabled: true,
      PreferredMfa: true,
    };
  } else {
    // Disable both
    smsMfaSettings = {
      Enabled: false,
      PreferredMfa: false,
    };

    totpMfaSettings = {
      Enabled: false,
      PreferredMfa: false,
    };
  }

  return new Promise((resolve, reject) => {
    cognitoUser.refreshSession(
      {
        getToken: () => refreshToken.token,
      },
      (err, _session) => {
        if (err) {
          reject(err);
          return;
        }

        cognitoUser.setUserMfaPreference(
          smsMfaSettings,
          totpMfaSettings,
          (err, result) => {
            if (err) {
              reject(err);
            } else {
              resolve(result);
            }
          },
        );
      },
    );
  });
};

export const disableMfa = (email, refreshToken) =>
  setUserMfaPreference(email, refreshToken);

export const setupCognitoPhoneNumber = async (
  email,
  refreshToken,
  phoneNumber,
) => {
  await init();
  const cognitoUser = createCognitoUser(email);

  const attributeNameToVerify = 'phone_number';
  const formattedPhoneNumber = formatToE164(phoneNumber);

  const attributeList = [
    new AmazonCognitoIdentity.CognitoUserAttribute({
      Name: attributeNameToVerify,
      Value: formattedPhoneNumber,
    }),
  ];

  return new Promise((resolve, reject) => {
    cognitoUser.refreshSession(
      {
        getToken: () => refreshToken.token,
      },
      (err, _session) => {
        if (err) {
          reject(err);
          return;
        }

        cognitoUser.updateAttributes(attributeList, (err, _result) => {
          if (err) {
            reject(err);
          } else {
            cognitoUser.getAttributeVerificationCode(attributeNameToVerify, {
              onSuccess: result => {
                resolve(result);
              },
              onFailure: err => {
                reject(err);
              },
              // Let the client handle the verification code input
              inputVerificationCode: null,
            });
          }
        });
      },
    );
  });
};

export const verifyCognitoPhoneNumber = async (
  email,
  refreshToken,
  verificationCode,
) => {
  await init();
  const cognitoUser = createCognitoUser(email);
  const attributeNameToVerify = 'phone_number';

  return new Promise((resolve, reject) => {
    cognitoUser.refreshSession(
      {
        getToken: () => refreshToken.token,
      },
      (err, _session) => {
        if (err) {
          reject(err);
          return;
        }

        cognitoUser.verifyAttribute(attributeNameToVerify, verificationCode, {
          onSuccess: _result => {
            setUserMfaPreference(email, refreshToken, MFA_TYPES.PHONE)
              .then(() => resolve('Phone code is verified'))
              .catch(err => reject(err));
          },
          onFailure: _err => {
            reject(new Error('Invalid code. Please try again.'));
          },
        });
      },
    );
  });
};

/**
 * Initiates TOTP setup for a user
 * @param {string} email - User's email
 * @param {Object} refreshToken - User's refresh token
 * @returns {Promise<string>} - Secret code for TOTP setup
 */
export const setupTOTP = async (email, refreshToken) => {
  await init();
  const cognitoUser = createCognitoUser(email);

  return new Promise((resolve, reject) => {
    cognitoUser.refreshSession(
      {
        getToken: () => refreshToken.token,
      },
      (err, _session) => {
        if (err) {
          reject(err);
          return;
        }

        cognitoUser.associateSoftwareToken({
          associateSecretCode: secretCode => {
            resolve(secretCode);
          },
          onFailure: err => {
            reject(err);
          },
        });
      },
    );
  });
};

/**
 * Verifies TOTP setup with the provided code
 * @param {string} email - User's email
 * @param {string} code - The verification code from authenticator app
 * @param {Object} refreshToken - User's refresh token
 * @returns {Promise<string>} - Success message if verification succeeds
 * @throws {Error} If verification fails
 */
export const verifyTOTP = async (email, code, refreshToken) => {
  await init();
  const cognitoUser = createCognitoUser(email);

  return new Promise((resolve, reject) => {
    cognitoUser.refreshSession(
      {
        getToken: () => refreshToken.token,
      },
      (err, _session) => {
        if (err) {
          reject(new Error('Session refresh failed'));
          return;
        }

        cognitoUser.verifySoftwareToken(code, 'ChiroTouch Authenticator', {
          onSuccess: _result => {
            setUserMfaPreference(email, refreshToken, MFA_TYPES.TOTP)
              .then(() => resolve('Authenticator app code is verified'))
              .catch(err => reject(err));
          },
          onFailure: _err => {
            reject(new Error('Invalid code. Please try again.'));
          },
        });
      },
    );
  });
};
